import React, { useEffect, useMemo, useState } from 'react';
import { Container, Paper, Typography, Box, Button, IconButton } from '@mui/material';
import { Favorite, FavoriteBorder } from '@mui/icons-material';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';

type Product = {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  category: string;
  variants?: { quantity: number; price: number }[];
};

const formatMXN = (value: number) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);

const ProductDetailPage: React.FC = () => {
  const { productId } = useParams<{ productId: string}>();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const [product, setProduct] = useState<Product | null>(null);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!db || !productId) { setLoading(false); return; }
    const ref = doc(db, 'products', productId);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) {
        setError('Producto no encontrado');
        setProduct(null);
        setLoading(false);
        return;
      }
      const data = snap.data() as any;
      const image = data.imageUrl || (Array.isArray(data.images) && data.images.length ? data.images[0] : 'https://via.placeholder.com/800x500?text=Producto');
      setProduct({
        id: snap.id,
        name: data.name || snap.id,
        description: data.description || '',
        price: typeof data.price === 'number' ? data.price : 0,
        image,
        category: (data.categoryId || '').toString(),
        variants: Array.isArray(data.variants) ? data.variants : undefined,
      });
      setLoading(false);
    }, (err) => {
      setError('No se pudo cargar el producto');
      setLoading(false);
    });
    return () => unsub();
  }, [productId]);

  // Seleccionar variante principal por defecto si existen variantes
  useEffect(() => {
    if (selectedVariantIndex == null && Array.isArray(product?.variants) && product!.variants!.length > 0) {
      setSelectedVariantIndex(0);
    }
    if (Array.isArray(product?.variants) && product!.variants!.length === 0) {
      setSelectedVariantIndex(null);
    }
  }, [product, selectedVariantIndex]);

  const displayPrice = useMemo(() => {
    if (!product) return 0;
    if (selectedVariantIndex == null) return product.price;
    if (selectedVariantIndex === 0) return product.price;
    const v = product.variants?.[selectedVariantIndex];
    return typeof v?.price === 'number' ? v.price : product.price;
  }, [product, selectedVariantIndex]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h5">Cargando producto…</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h5" sx={{ color: 'error.main' }}>{error}</Typography>
        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" component={Link} to="/catalog">Volver al catálogo</Button>
        </Box>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h5">Producto no disponible</Typography>
        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" component={Link} to="/catalog">Volver al catálogo</Button>
        </Box>
      </Container>
    );
  }


  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, md: 4 }, 
          borderRadius: 4,
          backgroundColor: '#fff',
          boxShadow: '0 8px 30px rgba(0,0,0,0.08)'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 6 }}>
          <Box sx={{ flex: 1, position: 'relative' }}>
            <Box 
              component="img" 
              src={product.image} 
              alt={product.name} 
              sx={{ 
                width: '100%', 
                borderRadius: 4,
                boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
                aspectRatio: '1/1',
                objectFit: 'cover'
              }} 
            />
            <IconButton
              onClick={() => toggleWishlist(product.id)}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                backgroundColor: 'rgba(255,255,255,0.8)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                color: isInWishlist(product.id) ? '#E91E63' : 'action.active',
                boxShadow: 2,
              }}
            >
              {isInWishlist(product.id) ? <Favorite /> : <FavoriteBorder />}
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography 
              variant="h3" 
              gutterBottom 
              sx={{ 
                fontFamily: '"Playfair Display", serif', 
                fontWeight: 700,
                color: '#1a1a1a',
                mb: 3
              }}
            >
              {product.name}
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#666', 
                mb: 4, 
                lineHeight: 1.8,
                fontSize: '1.1rem'
              }}
            >
              {product.description || 'Sin descripción disponible para este producto.'}
            </Typography>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 'bold', 
                color: 'primary.main', 
                mb: 4 
              }}
            >
              {formatMXN(displayPrice)}
            </Typography>

            {Array.isArray(product.variants) && product.variants.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Selecciona una opción:</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {product.variants.map((v, idx) => {
                    const selected = selectedVariantIndex === idx;
                    const variantPrice = idx === 0 ? product.price : v.price;
                    const label = `Cantidad ${v.quantity} — ${formatMXN(variantPrice)}`;
                    return (
                      <Button
                        key={idx}
                        variant={selected ? 'contained' : 'outlined'}
                        onClick={() => setSelectedVariantIndex(prev => (prev === idx ? null : idx))}
                        size="large"
                        fullWidth
                        sx={{ 
                          justifyContent: 'center',
                          borderRadius: '12px',
                          textTransform: 'none',
                          py: 1.5,
                          borderColor: selected ? 'transparent' : '#e0e0e0',
                          color: selected ? '#fff' : '#333',
                          backgroundColor: selected ? 'primary.main' : 'transparent',
                          '&:hover': {
                            backgroundColor: selected ? 'primary.dark' : '#f5f5f5',
                            borderColor: selected ? 'transparent' : '#bdbdbd'
                          }
                        }}
                      >
                        {label}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 'auto', pt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/catalog')}
                size="large"
                sx={{ 
                  flex: 1, 
                  py: 1.5,
                  borderRadius: '50px',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: 'rgba(233, 30, 99, 0.04)'
                  }
                }}
              >
                Volver
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  const variantLabel = ((): string | undefined => {
                    if (!Array.isArray(product.variants) || selectedVariantIndex == null) return undefined;
                    const v = product.variants[selectedVariantIndex];
                    const variantPrice = selectedVariantIndex === 0 ? product.price : v.price;
                    return `Cantidad ${v.quantity} — ${formatMXN(variantPrice)}`;
                  })();
                  addItem({ ...product, price: displayPrice, variantIndex: selectedVariantIndex, variantLabel }, 1);
                }}
                size="large"
                sx={{
                  flex: 2,
                  py: 1.5,
                  borderRadius: '50px',
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: 'primary.main',
                  boxShadow: '0 4px 15px rgba(233, 30, 99, 0.3)',
                  '&:hover': { 
                    backgroundColor: 'primary.dark',
                    boxShadow: '0 6px 20px rgba(233, 30, 99, 0.4)'
                  }
                }}
              >
                Agregar al Carrito
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default ProductDetailPage;